/* =============================================================
   MAGON HEADER — auto-hide on scroll, mobile drawer, search,
   mega menu interactions.
   ============================================================= */

class MagonHeader extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.stickyThreshold, 10) || 100;
    this.lastScroll = window.scrollY;
    this.ticking = false;

    this._initAutoHide();
    this._initMobileDrawer();
    this._initSearch();
    this._initCartIcon();
    this._initMegaTabbing();
    this._initMegaLazyLoad();
    this._initMobileMegaLazyLoad();
    this._updateHeaderGroupEnd();

    // Recalcula em resize (announcement marquee tem altura variável em diferentes viewports)
    this._onResize = () => this._updateHeaderGroupEnd();
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  // Calcula a posição vertical onde o header-group termina, pra mega menu fixed alinhar
  _updateHeaderGroupEnd() {
    const headerGroup = document.getElementById('header-group');
    if (!headerGroup) return;
    const rect = headerGroup.getBoundingClientRect();
    // bottom relativo ao viewport quando o header está no topo da página
    const offsetTop = headerGroup.offsetTop || 0;
    const totalHeight = headerGroup.offsetHeight || 0;
    document.documentElement.style.setProperty('--magon-header-group-end', `${offsetTop + totalHeight}px`);
  }

  // ── Auto-hide: scroll down esconde, scroll up mostra ──
  _initAutoHide() {
    this._onScroll = () => {
      if (this.ticking) return;
      window.requestAnimationFrame(() => {
        const current = window.scrollY;

        // Sempre visível antes do threshold
        if (current < this.threshold) {
          this.removeAttribute('data-hidden');
        } else if (current > this.lastScroll && current - this.lastScroll > 4) {
          // Scroll-down: esconde
          this.setAttribute('data-hidden', 'true');
        } else if (current < this.lastScroll && this.lastScroll - current > 4) {
          // Scroll-up: mostra
          this.removeAttribute('data-hidden');
        }
        this.lastScroll = current;
        this.ticking = false;
      });
      this.ticking = true;
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  // ── Mobile drawer ──
  _initMobileDrawer() {
    this.mobileDrawer = this.querySelector('.magon-mobile-drawer');
    if (!this.mobileDrawer) return;

    // Hoist drawer to <body> so it escapes the header's containing block
    // (.magon-header has `will-change: transform`, which traps `position: fixed` children).
    if (this.mobileDrawer.parentElement !== document.body) {
      document.body.appendChild(this.mobileDrawer);
    }

    this.querySelectorAll('[data-action="open-mobile-drawer"]').forEach(btn => {
      btn.addEventListener('click', () => this._openMobileDrawer());
    });

    // Close buttons live inside the drawer (panel close + backdrop)
    this.mobileDrawer.querySelectorAll('[data-action="close-mobile-drawer"]').forEach(btn => {
      btn.addEventListener('click', () => this._closeMobileDrawer());
    });

    // ESC fecha
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.mobileDrawer.getAttribute('aria-hidden') === 'false') {
        this._closeMobileDrawer();
      }
    });
  }

  _openMobileDrawer() {
    if (!this.mobileDrawer) return;
    this.mobileDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const trigger = this.querySelector('[data-action="open-mobile-drawer"]');
    trigger?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      this.mobileDrawer.querySelector('.magon-mobile-drawer__close')?.focus();
    });
  }

  _closeMobileDrawer() {
    if (!this.mobileDrawer) return;
    this.mobileDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    const trigger = this.querySelector('[data-action="open-mobile-drawer"]');
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
  }

  // ── Search ──
  _initSearch() {
    this.search = this.querySelector('magon-search');
    if (!this.search) return;

    const trigger = this.search.querySelector('[data-action="open-search"]');
    const closeBtn = this.search.querySelector('[data-action="close-search"]');
    const input = this.search.querySelector('[data-search-input]');

    trigger?.addEventListener('click', () => this._openSearch());
    closeBtn?.addEventListener('click', () => this._closeSearch());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.search.dataset.open === 'true') {
        this._closeSearch();
      }
    });

    // Predictive search
    let debounce;
    let activeFetch;
    input?.addEventListener('input', (e) => {
      clearTimeout(debounce);
      const q = e.target.value.trim();
      if (q.length < 2) {
        this._renderSearchEmpty();
        return;
      }
      this._renderSearchLoading();
      debounce = setTimeout(() => {
        if (activeFetch) activeFetch.abort();
        activeFetch = new AbortController();
        this._fetchPredictive(q, activeFetch.signal);
      }, 220);
    });

    // Click fora fecha
    this._onDocClick = (e) => {
      if (this.search.dataset.open !== 'true') return;
      if (!this.search.contains(e.target)) this._closeSearch();
    };
    document.addEventListener('click', this._onDocClick);
  }

  _openSearch() {
    this.search.dataset.open = 'true';
    const panel = this.search.querySelector('[data-search-panel]');
    const trigger = this.search.querySelector('[data-action="open-search"]');
    panel?.removeAttribute('hidden');
    trigger?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      this.search.querySelector('[data-search-input]')?.focus();
    });
  }

  _closeSearch() {
    this.search.dataset.open = 'false';
    const trigger = this.search.querySelector('[data-action="open-search"]');
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
    setTimeout(() => {
      this.search.querySelector('[data-search-panel]')?.setAttribute('hidden', '');
    }, 300);
  }

  _renderSearchEmpty() {
    this._toggleSearchState({ empty: true });
  }

  _renderSearchLoading() {
    this._toggleSearchState({ loading: true });
  }

  _toggleSearchState({ empty = false, loading = false, grid = false, noresults = false } = {}) {
    const els = {
      empty: this.search.querySelector('[data-search-empty]'),
      loading: this.search.querySelector('[data-search-loading]'),
      carousel: this.search.querySelector('[data-search-carousel]'),
      noresults: this.search.querySelector('[data-search-noresults]'),
      all: this.search.querySelector('[data-search-all]'),
    };
    els.empty?.toggleAttribute('hidden', !empty);
    els.loading?.toggleAttribute('hidden', !loading);
    els.carousel?.toggleAttribute('hidden', !grid);
    els.noresults?.toggleAttribute('hidden', !noresults);
    els.all?.toggleAttribute('hidden', !grid);

    // Quando o carrossel se torna visivel, atualiza estado das setas
    if (grid && els.carousel) {
      requestAnimationFrame(() => this._updateSearchArrowStates());
    }
  }

  _initSearchCarousel() {
    if (this._searchCarouselInited) return;
    this._searchCarouselInited = true;
    const grid = this.search.querySelector('[data-search-grid]');
    const prev = this.search.querySelector('[data-search-prev]');
    const next = this.search.querySelector('[data-search-next]');
    if (!grid || !prev || !next) return;

    const getStep = () => {
      const card = grid.querySelector('.magon-predictive-search-results__item, .magon-card');
      if (!card) return 240;
      const styles = window.getComputedStyle(grid);
      const gap = parseFloat(styles.gap || '16');
      return card.offsetWidth + gap;
    };

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    prev.addEventListener('click', () => grid.scrollBy({ left: -getStep(), behavior: reduced ? 'auto' : 'smooth' }));
    next.addEventListener('click', () => grid.scrollBy({ left: getStep(), behavior: reduced ? 'auto' : 'smooth' }));

    let raf = null;
    grid.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; this._updateSearchArrowStates(); });
    }, { passive: true });

    new ResizeObserver(() => this._updateSearchArrowStates()).observe(grid);
    window.addEventListener('resize', () => this._updateSearchArrowStates());
  }

  _updateSearchArrowStates() {
    const grid = this.search.querySelector('[data-search-grid]');
    const prev = this.search.querySelector('[data-search-prev]');
    const next = this.search.querySelector('[data-search-next]');
    if (!grid || !prev || !next) return;
    const maxScroll = grid.scrollWidth - grid.clientWidth;
    const noScroll = maxScroll <= 1;
    const atStart = grid.scrollLeft <= 1;
    const atEnd = grid.scrollLeft >= maxScroll - 1;
    prev.disabled = noScroll || atStart;
    next.disabled = noScroll || atEnd;
    prev.setAttribute('aria-disabled', prev.disabled);
    next.setAttribute('aria-disabled', next.disabled);
  }

  async _fetchPredictive(query, signal) {
    try {
      // Usa section rendering pra obter HTML pronto do magon-item (mesmo card do resto do site).
      // section_id = magon-predictive-search → sections/magon-predictive-search.liquid
      const url = `/search/suggest?q=${encodeURIComponent(query)}&section_id=magon-predictive-search&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
      const res = await fetch(url, { signal });
      const html = await res.text();

      // A resposta do section rendering vem embrulhada em <div id="shopify-section-magon-predictive-search">
      // Extraimos so o conteudo interno da nossa div .magon-predictive-search-results.
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const resultsContainer = doc.querySelector('.magon-predictive-search-results');
      const items = resultsContainer ? resultsContainer.querySelectorAll('.magon-predictive-search-results__item') : [];

      if (!items.length) {
        this._toggleSearchState({ noresults: true });
        return;
      }

      const grid = this.search.querySelector('[data-search-grid]');
      grid.innerHTML = '';
      items.forEach(item => grid.appendChild(item.cloneNode(true)));
      grid.scrollLeft = 0; // reseta a posição quando os resultados mudam
      this._initSearchCarousel();
      this._toggleSearchState({ grid: true });

      const allLink = this.search.querySelector('[data-search-all]');
      if (allLink) allLink.href = `/search?q=${encodeURIComponent(query)}&type=product`;
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Magon Search:', err);
      this._toggleSearchState({ noresults: true });
    }
  }

  // _renderSearchCard / _formatPrice / _esc / _attr removidos:
  // os cards agora vem renderizados pelo Liquid via section_id no /search/suggest
  // (sections/magon-predictive-search.liquid -> snippets/magon-item.liquid).

  // ── Cart icon ──
  _initCartIcon() {
    // Atualiza counter ao receber cart:update
    this._onCartUpdate = (e) => {
      const count = e?.detail?.resource?.item_count;
      if (typeof count === 'number') this._updateCartCount(count);
      else this._fetchCartCount();
    };
    document.addEventListener('cart:update', this._onCartUpdate);

    // Click no ícone do carrinho abre o drawer (não navega)
    const cartBtn = this.querySelector('[data-action="open-cart-drawer"]');
    cartBtn?.addEventListener('click', (e) => {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        e.preventDefault();
        drawer.open();
      }
    });
  }

  _updateCartCount(count) {
    const bubble = this.querySelector('[data-cart-count]');
    if (!bubble) return;
    bubble.textContent = count;
    bubble.toggleAttribute('hidden', count === 0);
  }

  async _fetchCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this._updateCartCount(cart.item_count);
    } catch {}
  }

  // ── Mega menu — lazy load no primeiro hover ──
  // Painéis começam vazios; conteúdo está em <template data-mega-template>
  // (browser não baixa imagens dentro de template). Clonamos no primeiro hover.
  _initMegaLazyLoad() {
    this.querySelectorAll('.magon-header__menu-item.has-mega').forEach(item => {
      const tpl = item.querySelector('[data-mega-template]');
      const panel = item.querySelector('[data-mega-panel]');
      if (!tpl || !panel) return;

      const load = () => {
        if (panel.dataset.loaded === 'true') return;
        panel.dataset.loaded = 'true';
        panel.appendChild(tpl.content.cloneNode(true));
        // Setup carrosséis recém-injetados
        panel.querySelectorAll('[data-mega-carousel]').forEach(wrap => this._wireMegaCarousel(wrap));
      };

      // Carrega no primeiro pointer hover OU foco por teclado OU touch
      item.addEventListener('pointerenter', load, { once: true });
      item.addEventListener('focusin', load, { once: true });
      item.addEventListener('touchstart', load, { once: true, passive: true });
    });
  }

  // Wire single carousel (chamado após inject do template)
  _wireMegaCarousel(wrap) {
    const track = wrap.querySelector('[data-mega-cards-track]');
    const prev = wrap.querySelector('.magon-slider__arrow--prev');
    const next = wrap.querySelector('.magon-slider__arrow--next');
    if (!track || !prev || !next) return;

    const stepAmount = () => {
      const item = track.firstElementChild;
      if (!item) return 240;
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.columnGap || style.gap || '16');
      return item.offsetWidth + gap;
    };

    const scroll = (dir) => {
      const max = track.scrollWidth - track.clientWidth;
      const atStart = track.scrollLeft <= 1;
      const atEnd = track.scrollLeft >= max - 1;
      if (dir > 0 && atEnd) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (dir < 0 && atStart) {
        track.scrollTo({ left: max, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: stepAmount() * dir, behavior: 'smooth' });
      }
    };

    prev.addEventListener('click', (e) => { e.preventDefault(); scroll(-1); });
    next.addEventListener('click', (e) => { e.preventDefault(); scroll(1); });

    // Toggle .is-overflow no wrap quando há conteúdo para rolar.
    // Permite que a CSS habilite/oculte setas que dependem de overflow real.
    const updateOverflow = () => {
      const hasOverflow = track.scrollWidth - track.clientWidth > 1;
      wrap.classList.toggle('is-overflow', hasOverflow);
    };
    updateOverflow();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(updateOverflow);
      ro.observe(track);
    } else {
      window.addEventListener('resize', updateOverflow);
    }
  }

  // ── Mega menu mobile — clona o mesmo <template> do desktop no <details> do drawer ──
  // Reutiliza tudo que já existe (markup + CSS de cards/subs + carousel JS).
  // Cada <details data-mobile-mega-index="X"> casa com o <template data-mega-link-index="X">.
  _initMobileMegaLazyLoad() {
    if (!this.mobileDrawer) return;
    const detailsEls = this.mobileDrawer.querySelectorAll('details[data-mobile-mega-index]');
    detailsEls.forEach(details => {
      const idx = details.dataset.mobileMegaIndex;
      const slot = details.querySelector('[data-mobile-mega-slot]');
      if (!slot) return;
      // Templates moram dentro do header desktop (this); o drawer foi movido para body
      // mas this.querySelector ainda alcança o <template> original.
      const tpl = this.querySelector(`template[data-mega-link-index="${idx}"]`);
      if (!tpl) return;

      const load = () => {
        if (slot.dataset.loaded === 'true') return;
        slot.dataset.loaded = 'true';
        slot.appendChild(tpl.content.cloneNode(true));
        // Carrosséis recém-injetados ganham swipe + medição de overflow
        slot.querySelectorAll('[data-mega-carousel]').forEach(wrap => this._wireMegaCarousel(wrap));
      };

      details.addEventListener('toggle', () => {
        if (details.open) load();
      });
    });
  }

  // ── Mega menu — abrir/fechar via teclado (hover já é CSS) ──
  _initMegaTabbing() {
    this.querySelectorAll('.magon-header__menu-item.has-mega').forEach(item => {
      const link = item.querySelector('.magon-header__menu-link');
      const panel = item.querySelector('[data-mega-panel]');
      if (!panel) return;

      // Mostra panel quando o item recebe focus (acessibilidade teclado)
      item.addEventListener('focusin', () => panel.removeAttribute('hidden'));
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) panel.setAttribute('hidden', '');
      });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('cart:update', this._onCartUpdate);
  }
}

if (!customElements.get('magon-header')) {
  customElements.define('magon-header', MagonHeader);
}

// Web component vazio só pro search (interactions ficam no MagonHeader parent)
if (!customElements.get('magon-search')) {
  customElements.define('magon-search', class extends HTMLElement {});
}
